import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { Card } from '../components/ui/card';
import { LoadingState } from '../components/EmptyStates';
import { EmptyTimeline } from '../components/EmptyStates';
import { ArrowLeft, Tag } from 'lucide-react';
import { RTILineChart } from '../components/charts/RTILineChart';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { format } from 'date-fns';
import { MotionDiv, staggerContainer, staggerItem, PageTransition, AnimatedNumber } from '../lib/motion-utils';
import { usePerformance } from '../hooks/usePerformance';
import { EnhancedShareButton } from '../components/EnhancedShareButton';
import { useSubscriptionPlan } from '../lib/use-subscription-plan';
import { InfoHint } from '../components/InfoHint';

export default function Timeline() {
  // Track component performance
  usePerformance('Timeline');
  
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const { features, loading: subLoading } = useSubscriptionPlan();

  useEffect(() => {
    if (subLoading) return;
    fetchHistory();
  }, [subLoading, features.rtiHistoryDays]);

  const fetchHistory = async () => {
    try {
      // TEMPORARY: Load history from local storage
      const storedCheckins = localStorage.getItem('tension_checkins');
      const checkins = storedCheckins ? JSON.parse(storedCheckins) : [];

      const rtiDays = features.rtiHistoryDays;
      const cutoffMs =
        rtiDays == null ? null : Date.now() - rtiDays * 24 * 60 * 60 * 1000;
      
      // Transform check-ins into history format
      const historyData = checkins
        .filter((checkin: any) => {
          const ts = checkin?.timestamp ?? checkin?.date;
          if (!ts) return false;
          const t = new Date(ts).getTime();
          if (!Number.isFinite(t)) return false;
          if (cutoffMs == null) return true;
          return t >= cutoffMs;
        })
        .map((checkin: any) => {
          const score = checkin.rtiScore || 0;
          const state = score <= 35 ? 'Stable' : score <= 60 ? 'Elastic' : 'High Tension';
          
          return {
            timestamp: checkin.timestamp ?? checkin.date,
            score: score,
            state: state,
            triggers: checkin.triggers || [],
          };
        });
      
      setHistory(historyData);
      
      // Original server code (commented out due to deployment issue)
      /*
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/rti-history`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const data = await response.json();
      setHistory(data.history || []);
      */
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = history
    .filter((item) => item.timestamp && typeof item.score === 'number')
    .map((item) => {
      try {
        return {
          date: format(new Date(item.timestamp), 'MMM d'),
          score: Math.max(0, Math.min(100, item.score)), // Ensure score is between 0-100
          state: item.state,
        };
      } catch (error) {
        console.error('Error formatting chart data:', error);
        return null;
      }
    })
    .filter(Boolean) as Array<{ date: string; score: number; state: string }>;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="min-h-screen tension-page-bg">
      <PageTransition>
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div>
            <h1 className="text-2xl font-light text-neutral-900 dark:text-neutral-100 mb-2 inline-flex items-center gap-1.5">
              Tension Score Timeline
              <InfoHint
                title="Tension score timeline"
                description="This chart shows how relationship tension changes over time so you can spot patterns instead of judging one day."
              />
            </h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Track your relational tension over time
            </p>
          </div>

          {history.length === 0 ? (
            <EmptyTimeline 
              onAction={() => navigate('/check-in')}
              actionLabel="Complete First Check-In"
            />
          ) : (
            <MotionDiv
              variants={staggerContainer}
              initial="initial"
              animate="animate"
            >
              {/* Chart */}
              <MotionDiv variants={staggerItem}>
                <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-medium mb-2 text-neutral-900 dark:text-neutral-100">
                        {features.rtiHistoryDays == null
                          ? 'All-Time Tension History'
                          : `${features.rtiHistoryDays}-Day Tension History`}
                      </h2>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        Track your relational tension over time
                      </p>
                    </div>

<RTILineChart data={chartData} />

                    {/* Legend */}
                    <div className="flex items-center justify-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span className="text-neutral-600 dark:text-neutral-400">Stable (0-35)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <span className="text-neutral-600 dark:text-neutral-400">Elastic (36-60)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span className="text-neutral-600 dark:text-neutral-400">High Tension (61+)</span>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <InfoHint
                        title="Color zones"
                        description="Green means calmer days, yellow means mixed/fragile days, and red means high-pressure days that need more care."
                      />
                    </div>
                  </div>
                </Card>
              </MotionDiv>

              {/* Recent Scores */}
              <MotionDiv variants={staggerItem}>
                <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
                  <h3 className="text-lg font-medium mb-4 text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-1.5">
                    Recent Check-Ins
                    <InfoHint
                      title="Recent Check-Ins"
                      description="Your latest entries with score and context tags. Use this list to connect events to score shifts."
                    />
                  </h3>
                  <div className="space-y-3">
                    {history.slice(-7).reverse().map((item, index) => (
                      <MotionDiv
                        key={index}
                        variants={staggerItem}
                        className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-4">
                            <div
                              className={`w-2 h-2 rounded-full mt-1 ${
                                item.state === 'Stable'
                                  ? 'bg-green-500'
                                  : item.state === 'Elastic'
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              }`}
                            />
                            <div>
                              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{item.state}</p>
                              <p className="text-xs text-neutral-600 dark:text-neutral-400">
                                {format(new Date(item.timestamp), 'MMM d, yyyy • h:mm a')}
                              </p>
                            </div>
                          </div>
                          <div className="text-2xl font-light text-neutral-900 dark:text-neutral-100">
                            <AnimatedNumber value={item.score} />
                          </div>
                        </div>
                        
                        {/* Triggers */}
                        {item.triggers && item.triggers.length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap mt-2 ml-6">
                            <Tag className="w-3 h-3 text-neutral-400 dark:text-neutral-600" aria-hidden="true" />
                            {item.triggers.map((trigger: string, triggerIndex: number) => (
                              <span
                                key={triggerIndex}
                                className="px-2 py-0.5 rounded-full text-xs bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300"
                              >
                                {trigger}
                              </span>
                            ))}
                          </div>
                        )}
                      </MotionDiv>
                    ))}
                  </div>
                </Card>
              </MotionDiv>

              {/* Share Button - Static Embedded */}
              <MotionDiv variants={staggerItem}>
                <EnhancedShareButton variant="prominent" />
              </MotionDiv>
            </MotionDiv>
          )}
        </div>
      </PageTransition>
    </div>
  );
}